import { apiClient } from "./client";

export type ApplicationStatus =
  | "preapproved"
  | "approved"
  | "declined"
  | "pending"
  | "expired"
  | "credit_freeze"
  | "KBA";

export interface ApplicantSchema {
  firstName: string;
  lastName: string;
  email: string;
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  zipCode?: string | null;
  SSN?: string | null;
  DOB?: string | null; // ISO date-time string
  phone?: string | null;
}

export interface ApplicationAccountSchema {
  accountId: string;
  availableCredit: number;
  creditLimit: number;
  status: string;
  name?: string | null;
  phone?: string | null;
  zipCode?: string | null;
}

export interface ApplicationConfigSchema {
  webhookUrl: string;
  sendWebhooks: boolean;
  availableOffers: ApplicationOfferConfigSchema[];
  selectedOffers: ApplicationOfferConfigSchema[];
  errorsCount: number;
  errorsType?: string | null;
  allowExtraCharges: boolean;
  allowRefunds: boolean;
  allowPartialRefunds: boolean;
  allowVoids: boolean;
  requireSignature: boolean;
  mdaPercentage: number;
  transactionMinAmount: number;
  transactionMaxAmount: number;
  applicationStatus: ApplicationStatus;
  transactionStatus: string;
  refundStatus: string;
  applicationAmountAdjustment: number;
  creditLimit: number;
  availableCredit: number;
  allowCrossLocationAccount: boolean;
  allowCrossApplicationAccount: boolean;
  errorsPerformed: number;
}

export interface ApplicationOfferConfigSchema {
  type: string;
  duration: number;
  downPayment?: number | null;
  apr: number;
  promoDuration?: number;
  promoApr?: number;
  id?: string;
}

export interface ApplicationTransactionSchema {
  internalId: string;
  externalId: string;
  locationName: string;
  applicationId: string;
  amount: number;
  downPayment: number;
  status: string;
  signUrl?: string | null;
  receiptUrl?: string | null;
  offer?: {
    id: string;
    amount: number;
    type: string;
    downPayment: number;
    apr: number;
    duration: number;
    monthlyPayment: number;
    financedAmount: number;
    promoDuration?: number | null;
    promoApr?: number | null;
  } | null;
  refundedAmount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationOutputSchema {
  internalId: string;
  externalId: string;
  firstName: string;
  lastName: string;
  email: string;
  locationName: string;
  configuration: ApplicationConfigSchema;
  transactions: ApplicationTransactionSchema[];
  status: ApplicationStatus;
  amount: number;
  account: ApplicationAccountSchema | null;
  qaNotes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationShortOutputSchema {
  internalId: string;
  externalId: string;
  firstName: string;
  lastName: string;
  email: string;
  locationName: string;
  status: ApplicationStatus;
  amount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface ApplicationsListResponse {
  items: ApplicationShortOutputSchema[];
  total: number;
  offset: number;
  limit: number;
}

export interface ApplicationCreateSchema {
  externalId?: string | null;
  applicant: ApplicantSchema;
  webhook?: string | null;
  amount: number;
  address2?: string | null;
  qaNotes?: string | null;
  accountId?: string | null;
}

export interface ApplicationUpdateSchema {
  status?: ApplicationStatus | null;
  amount?: number | null;
  qaNotes?: string | null;
}

export interface GetApplicationsParams {
  location_name?: string | null;
  status?: ApplicationStatus | null;
  offset?: number;
  limit?: number;
}

export interface ApplicationOfferConfigInputSchema {
  type: string;
  duration: number;
  downPayment?: number | null;
  apr: number;
  promoDuration?: number;
  promoApr?: number;
}

export interface ApplicationConfigUpdateSchema {
  webhookUrl?: string | null;
  sendWebhooks?: boolean | null;
  availableOffers?: ApplicationOfferConfigSchema[] | null;
  selectedOffers?: ApplicationOfferConfigSchema[] | null;
  errorsCount?: number | null;
  errorsType?: string | null;
  allowExtraCharges?: boolean | null;
  allowRefunds?: boolean | null;
  allowPartialRefunds?: boolean | null;
  allowVoids?: boolean | null;
  requireSignature?: boolean | null;
  mdaPercentage?: number | null;
  transactionMinAmount?: number | null;
  transactionMaxAmount?: number | null;
  applicationStatus?: ApplicationStatus | null;
  transactionStatus?: string | null;
  refundStatus?: string | null;
  applicationAmountAdjustment?: number | null;
  creditLimit?: number | null;
  availableCredit?: number | null;
  allowCrossLocationAccount?: boolean | null;
  allowCrossApplicationAccount?: boolean | null;
  errorsPerformed?: number | null;
}

export const applicationsApi = {
  /**
   * Get all applications with pagination and filters
   */
  getApplications: async (params?: GetApplicationsParams): Promise<ApplicationsListResponse> => {
    const response = await apiClient.get("/applications/", { params });
    return response.data;
  },

  /**
   * Create a new application
   * Note: x-location-name header is automatically added by apiClient interceptor
   */
  createApplication: async (data: ApplicationCreateSchema): Promise<ApplicationOutputSchema> => {
    const response = await apiClient.post("/applications/", data);
    return response.data;
  },

  /**
   * Get a single application by ID
   */
  getApplication: async (applicationId: string): Promise<ApplicationOutputSchema> => {
    const response = await apiClient.get(`/applications/${applicationId}`);
    return response.data;
  },

  /**
   * Update application
   */
  updateApplication: async (
    applicationId: string,
    data: ApplicationUpdateSchema
  ): Promise<ApplicationShortOutputSchema> => {
    const response = await apiClient.patch(`/applications/${applicationId}`, data);
    return response.data;
  },

  /**
   * Update application config
   */
  updateApplicationConfig: async (
    applicationId: string,
    data: ApplicationConfigUpdateSchema
  ): Promise<ApplicationOutputSchema> => {
    const response = await apiClient.patch(`/applications/${applicationId}/config`, data);
    return response.data;
  },

  /**
   * Add offer to application
   */
  addOffer: async (
    applicationId: string,
    data: ApplicationOfferConfigInputSchema
  ): Promise<ApplicationOutputSchema> => {
    const response = await apiClient.post(`/applications/${applicationId}/offer`, data);
    return response.data;
  },

  /**
   * Get available offers for an application
   */
  getApplicationOffers: async (
    applicationId: string,
    params: { amount: number; down_payment: number }
  ): Promise<
    Array<{
      id: string;
      amount: number;
      type: "FIXED_APR" | "SAC";
      downPayment: number;
      apr: number;
      duration: number;
      monthlyPayment: number;
      promoMonthlyPayment: number;
      financedAmount: number;
      promoDuration?: number | null;
      promoApr?: number | null;
    }>
  > => {
    const response = await apiClient.get(`/applications/${applicationId}/offers`, {
      params: {
        amount: params.amount,
        down_payment: params.down_payment,
      },
    });
    return response.data;
  },
};
